import { useState, useRef, useEffect } from 'react'

interface UsePopoverEditorOptions {
  /** Called when user clicks outside the popover (typically triggers save) */
  onClickOutside: () => void
  /** Called when user presses Escape */
  onCancel: () => void
  /** Ref to the popover element for click-outside detection */
  popoverRef: React.RefObject<HTMLElement | null>
  /** Ref to the anchor element for position calculation */
  anchorRef: React.RefObject<HTMLElement | null>
  /** Expected popover width for overflow calculation */
  popoverWidth?: number
  /** Expected popover height for overflow calculation */
  popoverHeight?: number
  /**
   * Whether to center the popover horizontally on the anchor element.
   * Default: false (aligns to left edge of anchor)
   */
  centerOnAnchor?: boolean
  /**
   * Delay in ms before attaching the click-outside listener.
   * Prevents the popover from immediately closing on the click that opened it.
   * Default: 100. Set to 0 for immediate attachment.
   */
  attachDelay?: number
}

interface UsePopoverEditorReturn {
  /** Calculated fixed position for the popover, or null if not yet calculated */
  position: { top: number; left: number } | null
}

/**
 * Shared behavior hook for popover-based inline editors (Company, Select, Options).
 *
 * Handles:
 * - Fixed position calculation relative to anchor element with viewport overflow protection
 * - Click outside popover → triggers onClickOutside (with configurable delay)
 * - Escape key → cancel
 */
export function usePopoverEditor({
  onClickOutside,
  onCancel,
  popoverRef,
  anchorRef,
  popoverWidth = 300,
  popoverHeight = 300,
  centerOnAnchor = false,
  attachDelay = 100
}: UsePopoverEditorOptions): UsePopoverEditorReturn {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

  // Use refs to always have the latest callbacks without re-registering effects
  const onClickOutsideRef = useRef(onClickOutside)
  useEffect(() => { onClickOutsideRef.current = onClickOutside }, [onClickOutside])

  // Calculate fixed position based on anchor element
  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect()

      let top = rect.bottom + 4
      let left = centerOnAnchor
        ? rect.left - popoverWidth / 2 + rect.width / 2
        : rect.left

      if (top + popoverHeight > window.innerHeight) {
        top = Math.max(8, rect.top - popoverHeight - 4)
      }
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8)
      }
      if (left < 8) left = 8

      setPosition({ top, left })
    }
  }, [anchorRef, popoverWidth, popoverHeight, centerOnAnchor])

  // Click outside → save (with optional delay to prevent immediate close)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        onClickOutsideRef.current()
      }
    }

    if (attachDelay > 0) {
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, attachDelay)
      return () => {
        clearTimeout(timer)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    } else {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [popoverRef, attachDelay])

  // Escape key → cancel
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onCancel])

  return {
    position
  }
}

